<script setup lang="ts">
import type { InputProps } from '@/types/ui';
import { Icon } from '@iconify/vue';
/**
 * UiSelect - Design System Select Component
 *
 * Dropdown for selecting from predefined options.
 *
 * WHEN TO USE (vs TextInput vs Radio):
 * - Predefined list of 4+ options
 * - When space is limited (collapses to single line)
 * - When options are mutually exclusive
 *
 * DON'T USE FOR:
 * - 2-3 options (use UiRadio for visibility)
 * - Free-form text entry (use UiTextInput)
 * - Multiple selections (use UiCheckbox group)
 * - Searchable/filterable options (consider autocomplete)
 *
 * OPTION COUNT GUIDELINES:
 * - 2-3 options: Consider UiRadio (all options visible)
 * - 4-10 options: UiSelect is ideal
 * - 10+ options: Consider searchable/autocomplete
 *
 * @example
 * const countries = [
 *   { value: 'us', label: 'United States' },
 *   { value: 'uk', label: 'United Kingdom' },
 * ];
 * <UiSelect v-model="country" :options="countries" label="Country" />
 */
import { computed, onMounted, onUnmounted, ref, useId } from 'vue';
import { IconLib } from '@/assets/icon-library';

export interface SelectOption {
   value: string | number;
   label: string;
   disabled?: boolean;
}

export interface UiSelectProps extends InputProps {
   /** Array of options */
   options: SelectOption[];
   /** Allow clearing selection */
   clearable?: boolean;
}

const props = withDefaults(defineProps<UiSelectProps>(), {
   size: 'md',
   disabled: false,
   required: false,
   clearable: false,
});

const modelValue = defineModel<string | number | null>();
const id = useId();
const isOpen = ref(false);
const selectRef = ref<HTMLDivElement | null>(null);

const hasError = computed(() => !!props.errorMessage);

const selectedOption = computed(() => {
   return props.options.find(opt => opt.value === modelValue.value);
});

const displayValue = computed(() => {
   return selectedOption.value?.label || props.placeholder || 'Select...';
});

const wrapperClasses = computed(() => {
   const sizeClasses = {
      sm: 'min-h-10',
      md: 'min-h-12',
      lg: 'min-h-14',
   };

   const baseClasses = [
      'flex items-center justify-between gap-2 w-full rounded-lg border-2 transition-colors cursor-pointer',
      'focus-within:ring-2 focus-within:ring-primary',
      sizeClasses[props.size],
   ];

   const paddingClasses = {
      sm: 'px-3',
      md: 'px-4',
      lg: 'px-5',
   };
   baseClasses.push(paddingClasses[props.size]);

   if (props.disabled) {
      baseClasses.push('opacity-50 cursor-not-allowed bg-neutral-light');
   }
   else {
      baseClasses.push('bg-white');
   }

   if (hasError.value) {
      baseClasses.push('border-accent focus-within:ring-accent');
   }
   else if (isOpen.value) {
      baseClasses.push('border-primary');
   }
   else {
      baseClasses.push('border-neutral hover:border-primary');
   }

   return baseClasses.join(' ');
});

const textClasses = computed(() => {
   const sizeClasses = {
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
   };

   return [
      'flex-1 truncate',
      sizeClasses[props.size],
      selectedOption.value ? 'text-black' : 'text-neutral-dark',
   ].join(' ');
});

const labelClasses = computed(() => {
   return [
      'block text-sm font-medium text-black mb-1.5',
      props.disabled ? 'opacity-50' : '',
   ].join(' ');
});

function toggleDropdown() {
   if (!props.disabled) {
      isOpen.value = !isOpen.value;
   }
}

function selectOption(option: SelectOption) {
   if (!option.disabled) {
      modelValue.value = option.value;
      isOpen.value = false;
   }
}

function clearSelection(event: MouseEvent) {
   event.stopPropagation();
   modelValue.value = null;
}

function handleClickOutside(event: MouseEvent) {
   if (selectRef.value && !selectRef.value.contains(event.target as Node)) {
      isOpen.value = false;
   }
}

onMounted(() => {
   document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
   document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
   <div ref="selectRef" class="relative flex flex-col">
      <!-- Label -->
      <label v-if="label" :for="id" :class="labelClasses">
         {{ label }}
         <span v-if="required" class="text-accent ml-0.5">*</span>
      </label>

      <!-- Select trigger -->
      <div
         :id="id"
         role="combobox"
         :aria-expanded="isOpen"
         :aria-haspopup="true"
         :aria-invalid="hasError"
         tabindex="0"
         :class="wrapperClasses"
         @click="toggleDropdown"
         @keydown.enter.prevent="toggleDropdown"
         @keydown.space.prevent="toggleDropdown"
         @keydown.escape="isOpen = false"
      >
         <span :class="textClasses">{{ displayValue }}</span>

         <!-- Clear button -->
         <button
            v-if="clearable && selectedOption && !disabled"
            type="button"
            class="p-1 -m-1 rounded hover:bg-neutral-light transition-colors"
            aria-label="Clear selection"
            @click="clearSelection"
         >
            <Icon icon="mdi:close" class="size-4 text-neutral-dark" />
         </button>

         <!-- Dropdown arrow -->
         <Icon
            :icon="IconLib.actions.arrow.down"
            class="size-5 text-neutral-dark transition-transform"
            :class="{ 'rotate-180': isOpen }"
         />
      </div>

      <!-- Dropdown menu -->
      <Transition
         enter-active-class="transition duration-100 ease-out"
         enter-from-class="opacity-0 -translate-y-1"
         enter-to-class="opacity-100 translate-y-0"
         leave-active-class="transition duration-75 ease-in"
         leave-from-class="opacity-100 translate-y-0"
         leave-to-class="opacity-0 -translate-y-1"
      >
         <ul
            v-if="isOpen"
            role="listbox"
            class="absolute z-50 top-full left-0 right-0 mt-1 py-1 bg-white border-2 border-neutral rounded-lg shadow-lg max-h-60 overflow-auto"
         >
            <li
               v-for="option in options"
               :key="option.value"
               role="option"
               :aria-selected="option.value === modelValue"
               :aria-disabled="option.disabled"
               class="px-4 py-2 cursor-pointer transition-colors"
               :class="[
                  option.disabled
                     ? 'opacity-50 cursor-not-allowed'
                     : 'hover:bg-primary/10',
                  option.value === modelValue
                     ? 'bg-primary/10 text-primary font-medium'
                     : 'text-black',
               ]"
               @click="selectOption(option)"
            >
               {{ option.label }}
            </li>
            <li v-if="options.length === 0" class="px-4 py-2 text-neutral-dark">
               No options available
            </li>
         </ul>
      </Transition>

      <!-- Helper text -->
      <span
         v-if="helperText && !errorMessage"
         class="text-sm text-neutral-dark mt-1.5"
      >
         {{ helperText }}
      </span>

      <!-- Error message -->
      <span
         v-if="errorMessage"
         class="text-sm text-accent mt-1.5"
         role="alert"
      >
         {{ errorMessage }}
      </span>
   </div>
</template>
